import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { ExportButton } from '../components/ExportButton';
import { SLASummaryCard } from '../components/SLAIndicator';
import { COLUMN_PRESETS } from '../services/exportService';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import {
  FileText, AlertTriangle, Clock, CheckCircle, TrendingUp, 
  ArrowUpRight, ArrowDownRight, Building2, Sparkles, Plus, 
  Rocket, Brain, Shield, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import IPIWidget from '../components/IPIWidget';

// Premium color palette - sophisticated gradients
const PREMIUM_COLORS = {
  primary: '#1d4ed8',
  primaryLight: '#8B5CF6',
  primaryDark: '#1e40af',
  success: '#10B981',
  successLight: '#34D399',
  warning: '#F59E0B',
  warningLight: '#FBBF24',
  danger: '#EF4444',
  dangerLight: '#F87171',
  slate: '#64748B',
  slateLight: '#94A3B8'
};

// Premium gradient colors for donut chart
const DONUT_GRADIENTS = [
  { id: 'donutBlue', start: '#3B82F6', end: '#1D4ED8' },
  { id: 'donutCyan', start: '#06B6D4', end: '#0891B2' },
  { id: 'donutEmerald', start: '#10B981', end: '#059669' },
  { id: 'donutViolet', start: '#8B5CF6', end: '#7C3AED' },
  { id: 'donutAmber', start: '#F59E0B', end: '#D97706' },
  { id: 'donutRose', start: '#F43F5E', end: '#E11D48' },
];

// Gradient definitions for charts
const GradientDefs = () => (
  <defs>
    <linearGradient id="gradientPrimary" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={PREMIUM_COLORS.primary} stopOpacity={0.9} />
      <stop offset="100%" stopColor={PREMIUM_COLORS.primaryLight} stopOpacity={0.6} />
    </linearGradient>
    <linearGradient id="gradientSuccess" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={PREMIUM_COLORS.success} stopOpacity={0.9} />
      <stop offset="100%" stopColor={PREMIUM_COLORS.successLight} stopOpacity={0.6} />
    </linearGradient>
    <linearGradient id="gradientWarning" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={PREMIUM_COLORS.warning} stopOpacity={0.9} />
      <stop offset="100%" stopColor={PREMIUM_COLORS.warningLight} stopOpacity={0.6} />
    </linearGradient>
    <linearGradient id="gradientDanger" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={PREMIUM_COLORS.danger} stopOpacity={0.9} />
      <stop offset="100%" stopColor={PREMIUM_COLORS.dangerLight} stopOpacity={0.6} />
    </linearGradient>
    <linearGradient id="gradientArea" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={PREMIUM_COLORS.primary} stopOpacity={0.3} />
      <stop offset="100%" stopColor={PREMIUM_COLORS.primary} stopOpacity={0.05} />
    </linearGradient>
    {/* Donut chart gradients */}
    {DONUT_GRADIENTS.map((g) => (
      <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={g.start} />
        <stop offset="100%" stopColor={g.end} />
      </linearGradient>
    ))}
  </defs>
);

// Custom tooltip with premium styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-2xl border border-slate-700/50">
        <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">{label || payload[0]?.name}</p>
        <p className="text-white text-lg font-bold">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

// Custom legend for donut chart - Premium style
const CustomLegend = ({ data }) => (
  <div className="flex flex-wrap justify-center gap-3 mt-4">
    {data.map((entry, index) => (
      <div key={index} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
        <div 
          className="w-3 h-3 rounded-full shadow-sm" 
          style={{ background: `linear-gradient(135deg, ${DONUT_GRADIENTS[index % DONUT_GRADIENTS.length].start}, ${DONUT_GRADIENTS[index % DONUT_GRADIENTS.length].end})` }}
        />
        <span className="text-xs text-slate-600 font-medium">{entry.name}</span>
        <span className="text-xs text-slate-400 font-semibold">({entry.value})</span>
      </div>
    ))}
  </div>
);

const statusLabels = {
  RECEBIDO: 'Recebido',
  CLASSIFICADO: 'Classificado',
  PLANEJADO: 'Planejado',
  EM_EXECUCAO: 'Em Execução',
  AGUARDANDO_EVIDENCIA: 'Aguard. Evidência',
  EM_REVISAO: 'Em Revisão',
  RESPONDIDO: 'Respondido',
  ENCERRADO: 'Encerrado',
  ARQUIVADO: 'Arquivado'
};

const severityGradients = {
  LOW: 'url(#gradientSuccess)',
  MEDIUM: 'url(#gradientWarning)',
  HIGH: 'url(#gradientWarning)',
  CRITICAL: 'url(#gradientDanger)'
};

const severityLabels = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  CRITICAL: 'Crítica'
};

export const DashboardPage = () => {
  const { api, user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentActs, setRecentActs] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animatedStats, setAnimatedStats] = useState({ total: 0, completed: 0, delayed: 0, critical: 0 });
  const [evolutionPeriod, setEvolutionPeriod] = useState('6'); // meses

  const fetchEvolution = async (months) => {
    try {
      const evolutionRes = await api.get(`/dashboard/evolution?months=${months}`);
      setEvolution(evolutionRes.data || []);
    } catch (error) {
      console.error('Error fetching evolution:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, actsRes, evolutionRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/acts?limit=5'),
          api.get(`/dashboard/evolution?months=${evolutionPeriod}`).catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setRecentActs(actsRes.data);
        setEvolution(evolutionRes.data || []);
        
        // Animate numbers
        const targetTotal = statsRes.data?.total_acts || 0;
        const targetCompleted = statsRes.data?.completed_percentage || 0;
        const targetDelayed = statsRes.data?.delayed_percentage || 0;
        const targetCritical = statsRes.data?.critical_percentage || 0;
        
        let current = { total: 0, completed: 0, delayed: 0, critical: 0 };
        const duration = 1000;
        const steps = 30;
        const interval = duration / steps;
        
        const timer = setInterval(() => {
          current = {
            total: Math.min(current.total + Math.ceil(targetTotal / steps), targetTotal),
            completed: Math.min(current.completed + targetCompleted / steps, targetCompleted),
            delayed: Math.min(current.delayed + targetDelayed / steps, targetDelayed),
            critical: Math.min(current.critical + targetCritical / steps, targetCritical)
          };
          setAnimatedStats({ ...current });
          
          if (current.total >= targetTotal) {
            clearInterval(timer);
            setAnimatedStats({ total: targetTotal, completed: targetCompleted, delayed: targetDelayed, critical: targetCritical });
          }
        }, interval);
        
        return () => clearInterval(timer);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api]);

  const typeData = stats?.acts_by_type ? Object.entries(stats.acts_by_type).map(([name, value]) => ({
    name: name.replace(/_/g, ' ').charAt(0) + name.replace(/_/g, ' ').slice(1).toLowerCase(),
    value,
    fullName: name.replace(/_/g, ' ')
  })) : [];

  const statusData = stats?.acts_by_status ? Object.entries(stats.acts_by_status).map(([name, value], index) => ({
    name: statusLabels[name] || name,
    value,
    color: [PREMIUM_COLORS.primary, PREMIUM_COLORS.success, PREMIUM_COLORS.warning, PREMIUM_COLORS.primaryLight, PREMIUM_COLORS.slate, PREMIUM_COLORS.successLight][index % 6]
  })) : [];

  const severityData = stats?.acts_by_severity ? Object.entries(stats.acts_by_severity).map(([name, value]) => ({
    name: severityLabels[name] || name,
    value,
    gradient: severityGradients[name] || 'url(#gradientPrimary)',
    originalName: name
  })) : [];

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-80 rounded-2xl" />
            <Skeleton className="h-80 rounded-2xl" />
          </div>
        </div>
      </AppLayout>
    );
  }

  // Empty State for new tenants
  if (!loading && stats?.total_acts === 0) {
    return (
      <AppLayout>
        <div className="min-h-[70vh] flex items-center justify-center" data-testid="empty-state">
          <div className="max-w-2xl mx-auto text-center px-6">
            {/* Hero Icon */}
            <div className="relative mb-8">
              <div className="w-32 h-32 mx-auto rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-blue-700 flex items-center justify-center shadow-2xl shadow-blue-500/30 transform hover:scale-105 transition-transform duration-500">
                <Rocket className="w-16 h-16 text-white" />
              </div>
            </div>

            {/* Welcome Message */}
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Bem-vindo ao <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">BlueGov</span>!
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Você está pronto para revolucionar a gestão de atos de controle do seu órgão.
              <br />Comece criando seu primeiro ato.
            </p>

            {/* CTA Button */}
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-blue-700 text-lg h-14 px-10 shadow-xl shadow-blue-500/25 rounded-xl mb-12"
              onClick={() => navigate('/acts/new')}
              data-testid="create-first-act-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Criar Primeiro Ato de Controle
            </Button>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              {[
                { icon: Brain, title: 'IA Inteligente', desc: 'Classifique atos automaticamente com inteligência artificial' },
                { icon: Shield, title: 'Conformidade Total', desc: 'Rastreabilidade completa e proteção ao gestor' },
                { icon: TrendingUp, title: 'Dashboards', desc: 'Visualize métricas e tome decisões em tempo real' }
              ].map((feature, i) => (
                <Card key={i} className="border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-5">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <feature.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">{feature.title}</h3>
                    <p className="text-sm text-slate-500">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Tips */}
            <div className="mt-10 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <p className="text-sm text-blue-800 font-medium mb-2">💡 Dica Rápida</p>
              <p className="text-slate-600">
                Após criar seu primeiro ato, utilize o <strong>Assistente de IA</strong> no canto inferior direito para tirar dúvidas e receber sugestões inteligentes.
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
            <p className="text-slate-500 mt-1">
              Visão geral dos atos de controle — <span className="font-medium text-slate-700">{user?.tenant_id}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/executive')}
              data-testid="open-executive-view-btn"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Visão Executiva
            </Button>
            <ExportButton
              data={recentActs}
              columns={COLUMN_PRESETS.acts}
              title="Relatório do Dashboard"
              filename="dashboard-bluegov"
              tenant={user?.tenant_id}
              generatedBy={user?.name}
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
            />
            {hasPermission('create') && (
              <Button
                className="bg-gradient-to-r from-[#1d4ed8] to-[#8B5CF6] hover:from-[#1e40af] hover:to-[#7C3AED] shadow-lg shadow-blue-500/25 transition-all duration-300"
                onClick={() => navigate('/acts/new')}
                data-testid="new-act-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Novo Ato
              </Button>
            )}
          </div>
        </div>

        {/* IPI — Índice de Proteção Institucional (KPI principal) */}
        <IPIWidget />

        {/* Stats Cards - Premium Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Total Acts - Hero Card - BlueGov Blue Gradient */}
          <Card className="col-span-1 md:col-span-2 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 border-0 shadow-2xl shadow-blue-500/20 overflow-hidden relative" data-testid="total-acts-card">
            {/* Decorative circles */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/5" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/5" />
            <div className="absolute top-1/2 right-1/4 w-20 h-20 rounded-full bg-cyan-400/10 blur-2xl" />
            <CardContent className="p-6 md:p-8 relative z-10">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-white/70 uppercase tracking-widest">Total de Atos</p>
                  <p className="text-5xl md:text-6xl font-bold text-white mt-3 tabular-nums drop-shadow-lg">{animatedStats.total}</p>
                </div>
                <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Sparkles className="w-7 h-7 text-cyan-300" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-lg shadow-emerald-400/50" />
                    <span className="text-xs text-white/70 uppercase tracking-wider">Concluídos</span>
                  </div>
                  <p className="text-2xl font-bold text-white tabular-nums">{animatedStats.completed.toFixed(1)}%</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-amber-400 shadow-lg shadow-amber-400/50" />
                    <span className="text-xs text-white/70 uppercase tracking-wider">Atrasados</span>
                  </div>
                  <p className="text-2xl font-bold text-white tabular-nums">{animatedStats.delayed.toFixed(1)}%</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-rose-400 shadow-lg shadow-rose-400/50" />
                    <span className="text-xs text-white/70 uppercase tracking-wider">Críticos</span>
                  </div>
                  <p className="text-2xl font-bold text-white tabular-nums">{animatedStats.critical.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avg Closure Card */}
          <Card className="group hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-500 border-slate-200/60 bg-white/80 backdrop-blur" data-testid="avg-closure-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-600/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-medium">
                  <ArrowDownRight className="w-3 h-3 mr-1" />
                  -12%
                </Badge>
              </div>
              <p className="text-4xl font-bold text-slate-900 tracking-tight">
                {stats?.avg_closure_days?.toFixed(0) || 0}
                <span className="text-lg font-normal text-slate-400 ml-1">dias</span>
              </p>
              <p className="text-sm text-slate-500 mt-1">Tempo médio de fechamento</p>
            </CardContent>
          </Card>

          {/* Recurrence Card */}
          <Card className="group hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-500 border-slate-200/60 bg-white/80 backdrop-blur" data-testid="recurrence-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500/10 to-orange-600/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <Badge className="bg-rose-50 text-rose-700 border-rose-200 font-medium">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  +5%
                </Badge>
              </div>
              <p className="text-4xl font-bold text-slate-900 tracking-tight">
                {stats?.recurrence_rate?.toFixed(0) || 0}
                <span className="text-lg font-normal text-slate-400 ml-1">%</span>
              </p>
              <p className="text-sm text-slate-500 mt-1">Taxa de reincidência</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section - Premium Design */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* SLA Summary Card */}
          <SLASummaryCard className="lg:col-span-1" />

          {/* Monthly Evolution Chart - NEW */}
          <Card className="lg:col-span-2 border-slate-200/60 bg-white/80 backdrop-blur shadow-lg shadow-blue-100/50" data-testid="evolution-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-800">Evolução Mensal</CardTitle>
                <Select 
                  value={evolutionPeriod} 
                  onValueChange={(value) => {
                    setEvolutionPeriod(value);
                    fetchEvolution(value);
                  }}
                >
                  <SelectTrigger className="w-[160px] h-8 text-sm bg-blue-50 border-blue-200 text-blue-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Último mês</SelectItem>
                    <SelectItem value="3">Últimos 3 meses</SelectItem>
                    <SelectItem value="6">Últimos 6 meses</SelectItem>
                    <SelectItem value="12">Último ano</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                {evolution.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={evolution} margin={{ left: -20, right: 20, top: 20, bottom: 10 }}>
                      <defs>
                        <linearGradient id="gradientTotal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="gradientCompleted" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#10B981" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="month" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748B', fontSize: 12, fontWeight: 500 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#94A3B8', fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="total" 
                        stroke="#3B82F6" 
                        strokeWidth={3}
                        fill="url(#gradientTotal)"
                        name="Total"
                        dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, fill: '#3B82F6', stroke: '#fff', strokeWidth: 2 }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="completed" 
                        stroke="#10B981" 
                        strokeWidth={3}
                        fill="url(#gradientCompleted)"
                        name="Concluídos"
                        dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    <div className="text-center">
                      <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Dados de evolução serão exibidos após criar atos</p>
                    </div>
                  </div>
                )}
              </div>
              {evolution.length > 0 && (
                <div className="flex justify-center gap-8 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-xs text-slate-600 font-medium">Total de Atos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-xs text-slate-600 font-medium">Concluídos</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Acts by Type - Horizontal Bars */}
          <Card className="border-slate-200/60 bg-white/80 backdrop-blur shadow-lg shadow-slate-200/50" data-testid="acts-by-type-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-800">Atos por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={typeData} layout="vertical" margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
                    <GradientDefs />
                    <XAxis 
                      type="number" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#94A3B8', fontSize: 12 }}
                    />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={100} 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748B', fontSize: 12, fontWeight: 500 }}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(110, 68, 255, 0.05)' }} />
                    <Bar 
                      dataKey="value" 
                      fill="url(#gradientPrimary)" 
                      radius={[0, 8, 8, 0]}
                      barSize={28}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Acts by Status - Premium Donut Chart with Gradients */}
          <Card className="border-slate-200/60 bg-white/80 backdrop-blur shadow-lg shadow-blue-100/50" data-testid="acts-by-status-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-800">Atos por Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {DONUT_GRADIENTS.map((g) => (
                        <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={g.start} />
                          <stop offset="100%" stopColor={g.end} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="45%"
                      innerRadius={65}
                      outerRadius={95}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                      animationBegin={0}
                      animationDuration={1000}
                    >
                      {statusData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={`url(#${DONUT_GRADIENTS[index % DONUT_GRADIENTS.length].id})`}
                          className="drop-shadow-md hover:opacity-90 transition-all cursor-pointer"
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center text */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ marginTop: '-20px' }}>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-slate-800">{statusData.reduce((acc, cur) => acc + cur.value, 0)}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total</p>
                  </div>
                </div>
              </div>
              <CustomLegend data={statusData} />
            </CardContent>
          </Card>

          {/* Acts by Severity - Vertical Bars with Gradients */}
          <Card className="border-slate-200/60 bg-white/80 backdrop-blur shadow-lg shadow-slate-200/50" data-testid="acts-by-severity-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-800">Atos por Severidade</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={severityData} margin={{ left: -20, right: 20, top: 20, bottom: 10 }}>
                    <GradientDefs />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748B', fontSize: 12, fontWeight: 500 }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#94A3B8', fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(110, 68, 255, 0.05)' }} />
                    <Bar 
                      dataKey="value" 
                      radius={[8, 8, 0, 0]}
                      barSize={50}
                    >
                      {severityData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.gradient}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                {severityData.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      item.originalName === 'LOW' ? 'bg-emerald-500' :
                      item.originalName === 'MEDIUM' ? 'bg-amber-500' :
                      item.originalName === 'HIGH' ? 'bg-orange-500' : 'bg-rose-500'
                    }`} />
                    <span className="text-xs text-slate-600 font-medium">{item.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top Organs - Premium List */}
          <Card className="border-slate-200/60 bg-white/80 backdrop-blur shadow-lg shadow-slate-200/50" data-testid="top-organs-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-800">Principais Órgãos Emissores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-5">
                {stats?.acts_by_body && Object.entries(stats.acts_by_body).slice(0, 5).map(([body, count], index) => {
                  const percentage = (count / (stats?.total_acts || 1)) * 100;
                  const colors = ['from-blue-500 to-blue-600', 'from-blue-500 to-cyan-500', 'from-emerald-500 to-teal-500', 'from-orange-500 to-amber-500', 'from-pink-500 to-rose-500'];
                  return (
                    <div key={body} className="group">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[index % colors.length]} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-semibold text-slate-800">{body}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-700">{count}</span>
                              <span className="text-xs text-slate-400">({percentage.toFixed(0)}%)</span>
                            </div>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full bg-gradient-to-r ${colors[index % colors.length]} rounded-full transition-all duration-1000 ease-out`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!stats?.acts_by_body || Object.keys(stats.acts_by_body).length === 0) && (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Building2 className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm">Nenhum dado disponível</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Acts - Premium Table */}
        <Card className="border-slate-200/60 bg-white/80 backdrop-blur shadow-lg shadow-slate-200/50" data-testid="recent-acts-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-800">Atos Recentes</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => navigate('/acts')}
              >
                Ver todos
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {recentActs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <FileText className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-sm">Nenhum ato encontrado</p>
                </div>
              ) : (
                recentActs.map((act, index) => (
                  <button
                    key={act.act_id}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-slate-50/80 transition-all duration-200 text-left group"
                    onClick={() => navigate(`/acts/${act.act_id}`)}
                    data-testid={`recent-act-${act.act_id}`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className={`w-3 h-3 rounded-full ring-4 transition-all duration-300 group-hover:scale-125 ${
                      act.severity === 'CRITICAL' ? 'bg-rose-500 ring-rose-100' :
                      act.severity === 'HIGH' ? 'bg-orange-500 ring-orange-100' :
                      act.severity === 'MEDIUM' ? 'bg-amber-500 ring-amber-100' : 'bg-emerald-500 ring-emerald-100'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-blue-600 transition-colors">
                        {act.control_body}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{act.type.replace(/_/g, ' ')}</p>
                    </div>
                    <Badge className={`font-medium transition-all duration-200 group-hover:scale-105 ${
                      act.status === 'ENCERRADO' || act.status === 'ARQUIVADO' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>
                      {statusLabels[act.status]}
                    </Badge>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default DashboardPage;
